import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { navigation, doctorInfo } from '../../data/mock';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100' 
          : 'bg-white border-b border-gray-100'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center transition-all duration-300 ${
          scrolled ? 'h-16' : 'h-20'
        }`}>
          {/* Logo */}
          <Link to="/" className="flex flex-col group">
            <span className={`font-semibold text-gray-900 tracking-tight transition-all duration-300 ${
              scrolled ? 'text-lg' : 'text-xl'
            }`}>
              {doctorInfo.name}
            </span>
            <span className="text-xs text-teal-700 font-medium group-hover:text-teal-600 transition-colors">
              Chirurgie mini-invasive
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 relative ${
                  isActive(item.href)
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {item.name}
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-teal-600" />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${doctorInfo.phone}`}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-teal-700 transition-colors group"
            >
              <span className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 transition-colors">
                <Phone className="w-4 h-4 text-teal-700" />
              </span>
              <span className="font-medium">{doctorInfo.phone}</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <div className="relative w-6 h-6">
              <span className={`absolute left-0 w-6 h-0.5 bg-gray-600 transition-all duration-300 ${
                mobileMenuOpen ? 'top-3 rotate-45' : 'top-1'
              }`} />
              <span className={`absolute left-0 top-3 w-6 h-0.5 bg-gray-600 transition-all duration-300 ${
                mobileMenuOpen ? 'opacity-0' : 'opacity-100'
              }`} />
              <span className={`absolute left-0 w-6 h-0.5 bg-gray-600 transition-all duration-300 ${
                mobileMenuOpen ? 'top-3 -rotate-45' : 'top-5'
              }`} />
            </div>
          </Button>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? 'max-h-96 pb-4' : 'max-h-0'
        }`}>
          <div className="flex flex-col gap-1 pt-2 border-t border-gray-100">
            {navigation.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                style={{ animationDelay: `${index * 50}ms` }}
                className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  mobileMenuOpen ? 'animate-fade-in-up' : ''
                } ${
                  isActive(item.href)
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <a
                href={`tel:${doctorInfo.phone}`}
                className="flex items-center gap-3 px-4 py-3 text-sm text-teal-700 font-medium bg-teal-50 rounded-lg"
              >
                <Phone className="w-4 h-4" />
                <span>{doctorInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
